/* eslint-disable react-hooks/exhaustive-deps */
import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';

import { DateRange } from '@material-ui/pickers';

import { FormControl, Grid, IconButton, MenuItem } from '@material-ui/core';

import { SearchOutlined } from '@material-ui/icons';

import { useStyles } from './styles';

import { useCommonStyles } from '../../../../../styles';

import { useApiError } from '../../../../../providers';

import { PartnerPartnership } from '../../../../../models';

import {
  DateRangeSelect,
  HamburgerIcon,
  SelectControl,
  SpinnerWithProps,
} from '../../../../../components';

import { FiltersDialog } from './FiltersDialog';

import { PeriodType } from '../../LinkStatisticsModel';

import { Link } from '../../../../RegistrationLinks/RegistrationLinksModel';

import {
  getClientShortCodes,
  getPartnerPartnerships,
} from '../../LinkStatisticsService';

interface Props {
  translation: any;
  link?: Link;
  partnershipCode?: string;
  dateRange: DateRange<Date>;
  handleDateRangeChange: (date: DateRange<Date>) => void;
  handleButtonClick?: () => void;
  onPartnershipChange: (newOptions: string | undefined) => void;
  onLinkChange: (newOptions: Link | undefined) => void;
  onPeriodChange: (newOptions: number | undefined) => void;
  handleFilterApplied: (
    partnership: string | undefined,
    period: number,
    link: Link | undefined,
  ) => void;
}

export const ReportToolbarWithFilters: FC<Props> = ({
  translation,
  link,
  partnershipCode,
  dateRange,
  handleDateRangeChange,
  handleButtonClick,
  onPartnershipChange,
  onLinkChange,
  onPeriodChange,
  handleFilterApplied,
}) => {
  const classes = useStyles();

  const commonClasses = useCommonStyles();

  const { addError } = useApiError();

  const [loading, setLoading] = useState<boolean>(false);

  const [selectedDateRange, setSelectedDateRange] =
    React.useState<DateRange<Date>>(dateRange);

  const [partnerships, setPartnerships] = useState<PartnerPartnership[]>([]);

  const [links, setLinks] = useState<Link[]>([]);

  const [selectedPartnership, setSelectedPartnership] = useState<string>();

  const [selectedPeriodType, setSelectedPeriodType] = useState<number>(
    PeriodType.Daily,
  );

  const [selectedLinkShortCode, setSelectedLinkShortCode] =
    useState<string>();

  const [openFilterDialog, setOpenFilterDialog] = useState(false);

  const getLinksData = useCallback(async (partnershipId: number) => {
    setLoading(true);

    setLinks([]);

    setSelectedLinkShortCode(undefined);

    onLinkChange(undefined);

    try {
      const response = await getClientShortCodes(partnershipId);

      const shortCodes = response.data?.shortCodes || [];

      const linksArr: Link[] = shortCodes.map(
        (shortCode: string) =>
          ({
            shortCode,
            link: shortCode,
          } as Link),
      );

      setLinks(linksArr);
    } catch (error: any) {
      setLinks([]);

      setSelectedLinkShortCode(undefined);

      onLinkChange(undefined);

      addError(JSON.stringify(error.response || error));
    } finally {
      setLoading(false);
    }
  }, [addError, onLinkChange]);

  const getPartnerships = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getPartnerPartnerships();

      setPartnerships(response.data);
    } catch (error: any) {
      addError(JSON.stringify(error.response));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await getPartnerships();
    })();
  }, []);

  useEffect(() => {
    if (handleButtonClick && partnerships.length > 0) {
      if (partnershipCode && link) {
        const partnership = partnerships?.find(
          (p) => p.partnershipCode === partnershipCode,
        );

        if (partnership) {
          setSelectedPartnership(partnership.partnershipCode);

          // eslint-disable-next-line no-void
          void getLinksData(partnership.id);

          setSelectedLinkShortCode(link.shortCode);

          handleFilterApplied(
            partnershipCode,
            selectedPeriodType,
            link,
          );
        }
      }

      if (partnerships.length === 1 && !partnershipCode) {
        setSelectedPartnership(partnerships[0].partnershipCode);

        // eslint-disable-next-line no-void
        void getLinksData(partnerships[0].id);

        onPartnershipChange(partnerships[0].partnershipCode);
      }
    } else {
      setSelectedPartnership('All');
    }
  }, [translation, partnershipCode, partnerships]);

  const requestReport = useCallback(() => {
    if (handleButtonClick) {
      handleButtonClick();
    }
  }, [handleButtonClick]);

  const handlePartnershipChange = useCallback(
    async (event: ChangeEvent<{ value: any }>) => {
      const { value } = event.target;

      if (value === 'All') {
        setSelectedPartnership('All');

        setLinks([]);

        setSelectedLinkShortCode(undefined);

        onPartnershipChange(undefined);

        onLinkChange(undefined);

        return;
      }

      const partnership = partnerships?.find(
        (p) => p.partnershipCode === value,
      );

      if (partnership) {
        setSelectedPartnership(partnership.partnershipCode);

        setSelectedLinkShortCode(undefined);

        onPartnershipChange(partnership.partnershipCode);

        onLinkChange(undefined);

        await getLinksData(partnership.id);
      }
    },
    [
      partnerships,
      getLinksData,
      onPartnershipChange,
      onLinkChange,
    ],
  );

  const handlePeriodChange = useCallback(
    async (event: ChangeEvent<{ value: any }>) => {
      setSelectedPeriodType(event.target.value);

      onPeriodChange(event.target.value);
    },
    [],
  );

  const handleLinkChange = useCallback(
    async (event: ChangeEvent<{ value: any }>) => {
      const link = links?.find(
        (l) => l.shortCode === event.target.value,
      );

      if (link) {
        setSelectedLinkShortCode(link.shortCode);

        onLinkChange(link);
      } else {
        setSelectedLinkShortCode(undefined);

        onLinkChange(undefined);
      }
    },
    [links],
  );

  return (
    <>
      <Grid
        container
        direction="row"
        justify="space-between"
        alignContent="center"
        alignItems="center"
        className={classes.desktopContainer}
        spacing={1}
      >
        <SpinnerWithProps isLoading={loading} />

        <Grid item className={classes.filters}>
          <p className={commonClasses.formControlTitle}>
            {translation.text_6166}
          </p>

          <DateRangeSelect
            defaultValue={selectedDateRange}
            onRangeChange={(value) => {
              setSelectedDateRange(value);

              handleDateRangeChange(value);
            }}
          />
        </Grid>

        <Grid item className={classes.filters}>
          <FormControl
            variant="outlined"
            className={commonClasses.formControl}
          >
            <p className={commonClasses.formControlTitle}>
              {translation.period_type}
            </p>

            <SelectControl
              value={selectedPeriodType}
              onChange={handlePeriodChange}
              minWidth={97}
            >
              <MenuItem
                className={commonClasses.optionSelector}
                style={{ width: 'inherit' }}
                value={PeriodType.Weekly}
                key={PeriodType.Weekly}
              >
                {translation.weekly}
              </MenuItem>

              <MenuItem
                className={commonClasses.optionSelector}
                style={{ width: 'inherit' }}
                key={PeriodType.Daily}
                value={PeriodType.Daily}
              >
                {translation.text_6421}
              </MenuItem>
            </SelectControl>
          </FormControl>
        </Grid>

        <Grid item className={classes.filters}>
          {partnerships && partnerships.length !== 0 && (
            <FormControl
              variant="outlined"
              className={commonClasses.formControl}
            >
              <p className={commonClasses.formControlTitle}>
                {translation.text_6275}
              </p>

              <SelectControl
                value={selectedPartnership ?? 'All'}
                onChange={handlePartnershipChange}
                minWidth={97}
              >
                <MenuItem
                  key="show_all"
                  className={commonClasses.optionSelector}
                  style={{ width: 'inherit' }}
                  value="All"
                >
                  {translation.show_all}
                </MenuItem>

                {partnerships.map((partnership) => (
                  <MenuItem
                    className={commonClasses.optionSelector}
                    style={{ width: 'inherit' }}
                    key={partnership.id}
                    value={partnership.partnershipCode}
                  >
                    {partnership.partnershipCode}
                  </MenuItem>
                ))}
              </SelectControl>
            </FormControl>
          )}
        </Grid>

        <Grid item className={classes.filters}>
          {selectedPartnership &&
            selectedPartnership !== 'All' && (
              <FormControl
                variant="outlined"
                className={commonClasses.formControl}
              >
                <p className={commonClasses.formControlTitle}>
                  {translation.links}
                </p>

                <SelectControl
                  value={selectedLinkShortCode ?? 'All'}
                  onChange={handleLinkChange}
                  minWidth={97}
                >
                  <MenuItem
                    key="show_all"
                    className={commonClasses.optionSelector}
                    style={{ width: 'inherit' }}
                    value="All"
                  >
                    {translation.show_all}
                  </MenuItem>

                  {links.map((link) => (
                    <MenuItem
                      className={commonClasses.optionSelector}
                      style={{ width: 'inherit' }}
                      key={link.shortCode}
                      value={link.shortCode}
                    >
                      {link.shortCode}
                    </MenuItem>
                  ))}
                </SelectControl>
              </FormControl>
            )}
        </Grid>

        <Grid item style={{ alignSelf: 'flex-end' }}>
          <IconButton onClick={requestReport}>
            <SearchOutlined style={{ color: '#030C21' }} />
          </IconButton>
        </Grid>
      </Grid>

      <Grid
        container
        direction="row"
        justify="space-between"
        className={classes.mobileContainer}
        alignItems="center"
      >
        <Grid item xs={11}>
          <p className={commonClasses.formControlTitle}>
            {translation.text_6166}
          </p>

          <DateRangeSelect
            defaultValue={selectedDateRange}
            onRangeChange={(value) => {
              setSelectedDateRange(value);

              handleDateRangeChange(value);
            }}
          />
        </Grid>

        <Grid item xs={1}>
          <IconButton onClick={() => setOpenFilterDialog(true)}>
            <HamburgerIcon />
          </IconButton>
        </Grid>
      </Grid>

      <FiltersDialog
        open={openFilterDialog}
        translation={translation}
        partnerships={partnerships}
        partnership={
          selectedPartnership === 'All'
            ? undefined
            : selectedPartnership
        }
        partnershipLinks={links}
        onClose={() => setOpenFilterDialog(false)}
        applyFilter={(partnership, period, link) => {
          setOpenFilterDialog(false);

          setSelectedPartnership(partnership || 'All');

          setSelectedPeriodType(period);

          setSelectedLinkShortCode(link?.shortCode);

          handleFilterApplied(partnership, period, link);
        }}
      />
    </>
  );
};
